import React, { useEffect, useRef, useState } from 'react';
import Svg from 'components/utils/Svg/Svg';
import { imageLinks } from 'assets/images';
import WaveformVisualizer from '../../ChatWidgetInput/VoiceRecordingInterface/WaveformVisualizer/WaveformVisualizer';
import './VoiceNote.scss';

/** Decode base64 (data-URI tolerated) to bytes. */
function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/^data:.*;base64,/, '').replace(/\s+/g, '');
  const bin = typeof atob === 'function' ? atob(clean) : Buffer.from(clean, 'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Quick file-type sniff (good enough for WAV/MP3/OGG). */
function sniffMime(bytes: Uint8Array): string {
  const s4 = Array.from(bytes.slice(0, 4), b => String.fromCharCode(b)).join('');
  if (s4 === 'RIFF' && Array.from(bytes.slice(8, 12), b => String.fromCharCode(b)).join('') === 'WAVE') return 'audio/wav';
  if (s4 === 'OggS') return 'audio/ogg';
  if (s4 === 'ID3' || (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0)) return 'audio/mpeg';
  return 'application/octet-stream';
}

/** Build a playable Object URL from base64; returns {url|error, mime, byteLen}. */
export function makeAudioUrlFromBase64(b64: string) {
  try {
    const bytes = base64ToBytes(b64);
    const mime = sniffMime(bytes);
    // Use bytes directly with type assertion to avoid TypeScript issues
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
    return { url, mime, byteLen: bytes.length } as const;
  } catch (e: any) {
    return { error: e?.message ?? 'decode failed' } as const;
  }
}

/** Check if a string is likely base64 audio data */
function isBase64Audio(data: string | Blob): data is string {
  if (typeof data !== 'string') return false;
  // Check if it's a data URI
  if (data.startsWith('data:audio/')) return true;
  // Check if it's a long base64 string (likely audio)
  if (data.length > 100 && /^[A-Za-z0-9+/=]+$/.test(data.replace(/^data:.*;base64,/, ''))) {
    return true;
  }
  return false;
}

interface Base64AudioPlayerProps {
  audioSource: string | Blob;
  autoPlay?: boolean;
  uploadState?: string;
  errorMessage?: string;
  onRetry?: () => void;
  onResponse?: (resp: { ok: boolean; mime?: string; byteLen?: number; error?: string }) => void;
  pausePlaybackSignal?: number;
}

/** Audio player that accepts base64, Blob, or URL and matches VoiceNote look and feel. */
export default function Base64AudioPlayer({
  audioSource,
  autoPlay = false,
  uploadState,
  errorMessage,
  onRetry,
  onResponse,
  pausePlaybackSignal = 0,
}: Base64AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [err, setErr] = useState<string>();
  const audioRef = useRef<HTMLAudioElement>(null);

  // Convert audioSource to URL
  useEffect(() => {
    let objectUrl: string | null = null;

    if (isBase64Audio(audioSource)) {
      // Handle base64 string
      const res = makeAudioUrlFromBase64(audioSource);
      if ('error' in res) {
        setErr(res.error);
        onResponse?.({ ok: false, error: res.error });
        return;
      }
      setAudioUrl(res.url);
      objectUrl = res.url;
      onResponse?.({ ok: true, mime: res.mime, byteLen: res.byteLen });
    } else if (audioSource instanceof Blob) {
      // Handle Blob
      const url = URL.createObjectURL(audioSource);
      setAudioUrl(url);
      objectUrl = url;
    } else if (typeof audioSource === 'string') {
      // Handle URL string
      setAudioUrl(audioSource);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [audioSource, onResponse]);

  // Load audio duration and set up event listeners
  useEffect(() => {
    if (!audioUrl) return;

    const audio = audioRef.current;
    if (!audio) return;

    // Explicitly set playback rate to 1.0 to prevent speed issues
    audio.playbackRate = 1.0;

    const loadAudioDuration = async () => {
      try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioContext = new (window.AudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        setDuration(audioBuffer.duration);
        audioContext.close();
      } catch (error) {
        console.error('Error loading audio duration:', error);
      }
    };

    loadAudioDuration();

    const handleTimeUpdate = () => {
      const currentTimeValue = audio.currentTime || 0;
      setCurrentTime(currentTimeValue);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handlePlay = () => {
      // Ensure playback rate is set when playing
      audio.playbackRate = 1.0;
      setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);

    const handleLoadedMetadata = () => {
      // Ensure playback rate is set when metadata is loaded
      audio.playbackRate = 1.0;
    };

    const handleCanPlay = () => {
      // Ensure playback rate is set when audio can play
      audio.playbackRate = 1.0;
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);

    if (autoPlay && audio.paused) {
      audio.playbackRate = 1.0;
      audio.play().catch(error => {
        console.error('Error auto-playing audio:', error);
      });
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [audioUrl, autoPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!pausePlaybackSignal || !audio || audio.paused) {
      return;
    }

    audio.pause();
    setIsPlaying(false);
  }, [pausePlaybackSignal]);

  useEffect(() => {
    const audioElement = audioRef.current;

    return () => {
      if (!audioElement) {
        return;
      }

      audioElement.pause();
      audioElement.removeAttribute('src');
      audioElement.load();
    };
  }, []);

  const handlePlayPause = () => {
    if (audioRef.current) {
      // Explicitly set playback rate before playing
      audioRef.current.playbackRate = 1.0;
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (progress: number) => {
    if (audioRef.current && duration > 0) {
      const newTime = progress * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatDuration = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Render upload state indicators as an overlay
  const renderUploadState = () => {
    if (uploadState === 'uploading') {
      return (
        <div className="upload-overlay uploading">
          <div className="spinner"></div>
        </div>
      );
    } else if (uploadState === 'failed') {
      return (
        <div className="retry-icon-container">
          <button className="retry-icon" onClick={onRetry} aria-label="Retry upload">
            <Svg src={imageLinks.icon.error || ''} />
          </button>
        </div>
      );
    }
    return null;
  };

  if (err) {
    return (
      <div className="voice-note" role="alert">
        <div className="voice-note-content">
          <span>Invalid/unsupported audio: {err}</span>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return (
      <div className="voice-note">
        <div className="voice-note-content">
          <span>Preparing audio…</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`voice-note ${uploadState ? `state-${uploadState}` : ''}`}>
      <div className="voice-note-content">
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          style={{ display: 'none' }}
        />
        <button
          className="playback-button"
          onClick={handlePlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          disabled={uploadState === 'uploading'}
        >
          <Svg src={isPlaying ? imageLinks.icon.pbPause : imageLinks.icon.playIcon} />
        </button>
        <div className="wave--form-group">
          <WaveformVisualizer
            audioUrl={audioUrl}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onSeek={handleSeek}
            duration={duration || 0}
            formattedDuration={formatDuration(duration || 0)}
            currentProgress={duration > 0 ? Math.min(currentTime / duration, 1) : 0}
          />
          <span className="duration-text">
            {!isPlaying && currentTime === 0
              ? formatDuration(duration)
              : `${formatDuration(currentTime)}`
            }
          </span>
        </div>
      </div>
      {renderUploadState()}
    </div>
  );
}
